import { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { InactivityTimeout } from './components/layout/InactivityTimeout';
import { UsageBeacon } from './components/layout/UsageBeacon';
import { useSimulationStore, useSessionStore } from './stores';
import { useToast } from './components/layout/Toast';
import { reportCrash } from './lib/crash';

// Eagerly loaded (always needed)
import { AppShell } from './components/layout/AppShell';
import { LandingPage } from './components/landing/LandingPage';
import { WeeklyDrillCard } from './components/training/WeeklyDrillCard';
import { CertificationPathCard } from './components/training/CertificationPathCard';
import { YouStrip } from './components/dashboard/YouStrip';
import { BriefingLine } from './components/dashboard/BriefingLine';
import { CoverageBanner } from './components/dashboard/CoverageBanner';
import { useSessionLauncher } from './hooks/useSessionLauncher';

// Lazy loaded (route-level code splitting)
const LoginPage = lazy(() => import('./components/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('./components/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const JoinPage = lazy(() => import('./components/auth/JoinPage').then(m => ({ default: m.JoinPage })));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const SharedReportPage = lazy(() => import('./components/auth/SharedReportPage').then(m => ({ default: m.SharedReportPage })));
const VerifyCertificationPage = lazy(() => import('./components/auth/VerifyCertificationPage').then(m => ({ default: m.VerifyCertificationPage })));
const ScenarioSelect = lazy(() => import('./components/simulation/ScenarioSelect').then(m => ({ default: m.ScenarioSelect })));
const SimulationView = lazy(() => import('./components/simulation/SimulationView').then(m => ({ default: m.SimulationView })));
const SessionHistory = lazy(() => import('./components/simulation/SessionHistory').then(m => ({ default: m.SessionHistory })));
const DebriefView = lazy(() => import('./components/simulation/DebriefView').then(m => ({ default: m.DebriefView })));
const DepartmentView = lazy(() => import('./components/department/DepartmentView').then(m => ({ default: m.DepartmentView })));
const TrainingHub = lazy(() => import('./components/training/TrainingHub').then(m => ({ default: m.TrainingHub })));
const InsightsDashboard = lazy(() => import('./components/insights/InsightsDashboard').then(m => ({ default: m.InsightsDashboard })));
const CommunityConnect = lazy(() => import('./components/community/CommunityConnect').then(m => ({ default: m.CommunityConnect })));
const CivilianChallengePage = lazy(() => import('./components/community/CivilianChallengePage').then(m => ({ default: m.CivilianChallengePage })));
const BreakdownViewPage = lazy(() => import('./components/breakdown/BreakdownViewPage').then(m => ({ default: m.BreakdownViewPage })));
const OpsPage = lazy(() => import('./components/ops/OpsPage').then(m => ({ default: m.OpsPage })));
const CrmPage = lazy(() => import('./components/ops/crm/CrmPage').then(m => ({ default: m.CrmPage })));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AboutPage = lazy(() => import('./components/pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./components/pages/ContactPage').then(m => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./components/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./components/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PaymentSuccessPage = lazy(() => import('./components/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentFailedPage = lazy(() => import('./components/pages/PaymentFailedPage').then(m => ({ default: m.PaymentFailedPage })));
const GrantsPage = lazy(() => import('./components/pages/GrantsPage').then(m => ({ default: m.GrantsPage })));
const DeleteAccountPage = lazy(() => import('./components/pages/DeleteAccountPage').then(m => ({ default: m.DeleteAccountPage })));
const DemoExperience = lazy(() => import('./components/demo/DemoExperience').then(m => ({ default: m.DemoExperience })));

function RouteSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-copapp-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Pins the dark theme variables for pages not yet migrated to semantic
 * text tokens, so they stay legible when the global theme is light.
 * Remove per page as each gets its light-mode pass.
 */
function DarkPinned({ children }: { children: React.ReactNode }) {
  return (
    <div className="theme-dark" style={{ display: 'contents' }}>
      {children}
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <InactivityTimeout />
      <UsageBeacon />
      <Routes>
        {/* Public — dark-pinned until each page gets its light-mode pass */}
        <Route path="/" element={<DarkPinned><LandingPage /></DarkPinned>} />
        <Route path="/login" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><LoginPage /></DarkPinned></Suspense>} />
        <Route path="/signup" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><SignupPage /></DarkPinned></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><ResetPasswordPage /></DarkPinned></Suspense>} />
        <Route path="/join" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><JoinPage /></DarkPinned></Suspense>} />
        <Route path="/shared-report/:reportId" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><SharedReportPage /></DarkPinned></Suspense>} />
        <Route path="/verify-certification/:officerId" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><VerifyCertificationPage /></DarkPinned></Suspense>} />
        <Route path="/about" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><AboutPage /></DarkPinned></Suspense>} />
        <Route path="/contact" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><ContactPage /></DarkPinned></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><PrivacyPage /></DarkPinned></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><TermsPage /></DarkPinned></Suspense>} />
        <Route path="/delete-account" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><DeleteAccountPage /></DarkPinned></Suspense>} />
        <Route path="/payment-success" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><PaymentSuccessPage /></DarkPinned></Suspense>} />
        <Route path="/payment-failed" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><PaymentFailedPage /></DarkPinned></Suspense>} />
        <Route path="/grants" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><GrantsPage /></DarkPinned></Suspense>} />
        {/* The public ride-along — the whole product, no sign-up, no Grok
            (scripted; docs/DEMO.md). Embedded on the landing page in device
            frames and standalone at copapp.ai/demo. */}
        <Route path="/demo" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><DemoExperience /></DarkPinned></Suspense>} />
        <Route path="/challenge/:challengeId" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><CivilianChallengePage /></DarkPinned></Suspense>} />
        <Route path="/breakdown/:breakdownId" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><BreakdownViewPage /></DarkPinned></Suspense>} />
        <Route path="/ops" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><OpsPage /></DarkPinned></Suspense>} />
        <Route path="/ops/crm" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><CrmPage /></DarkPinned></Suspense>} />
        <Route path="/ops/crm/:agencyId" element={<Suspense fallback={<RouteSpinner />}><DarkPinned><CrmPage /></DarkPinned></Suspense>} />

        {/* Community Connect is the open-system front door: public when
            signed out, in the app shell when signed in. Publish stays
            auth-gated inside the component. */}
        <Route path="/community" element={<Suspense fallback={<RouteSpinner />}><CommunityRoute /></Suspense>} />

        {/* Protected app with sidebar layout (AppShell + Settings are theme-aware) */}
        <Route element={<Protected><AppShell /></Protected>}>
          <Route path="/dashboard" element={<Suspense fallback={<RouteSpinner />}><Dashboard /></Suspense>} />
          <Route path="/sim" element={<Suspense fallback={<RouteSpinner />}><SimulationRouter /></Suspense>} />
          <Route path="/training" element={<Suspense fallback={<RouteSpinner />}><TrainingPage /></Suspense>} />
          <Route path="/department" element={<Suspense fallback={<RouteSpinner />}><DepartmentPage /></Suspense>} />
          <Route path="/insights" element={<Suspense fallback={<RouteSpinner />}><InsightsPage /></Suspense>} />
          <Route path="/settings" element={<Suspense fallback={<RouteSpinner />}><SettingsWrapper /></Suspense>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="w-8 h-8 border-2 border-copapp-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Civilian-challenge players hold anonymous Firebase sessions — they are
  // signed in but must never reach the officer shell (CommunityRoute already
  // enforces this; the officer routes were the gap).
  if (user?.isAnonymous) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Page wrappers (consistent padding, max-width)
// ---------------------------------------------------------------------------

function PageWrapper({ title, children, wide = false }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="p-6">
      <div className={`${wide ? 'max-w-7xl' : 'max-w-4xl'} mx-auto`}>
        <h1 className="text-2xl font-bold mb-6 text-fg">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/**
 * JoinFlashBanner — one-shot dismissible banner shown after a post-signup
 * join via department code. Reads sessionStorage 'copapp-join-flash' once,
 * removes after read, and shows the covered/refund message.
 */
function JoinFlashBanner() {
  const [flash, setFlash] = useState<{
    departmentName: string;
    canceledIndividual: boolean;
    refundedUsd: number | null;
  } | null>(() => {
    try {
      const raw = sessionStorage.getItem('copapp-join-flash');
      if (!raw) return null;
      sessionStorage.removeItem('copapp-join-flash');
      return JSON.parse(raw);
    } catch {
      return null;
    }
  });

  if (!flash) return null;

  return (
    <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm text-emerald-400 font-medium">
          You&apos;re in — covered by {flash.departmentName}.
        </p>
        {flash.canceledIndividual && (
          <p className="text-xs text-amber-400 mt-1">
            {flash.refundedUsd != null
              ? `Your individual subscription was canceled and $${flash.refundedUsd.toFixed(2)} refunded to your card — the department covers you now.`
              : "Your individual subscription was canceled — the department covers you now. (Refund processing; contact alexi@copapp.ai if it doesn't appear in a few days.)"}
          </p>
        )}
      </div>
      <button
        onClick={() => setFlash(null)}
        className="px-2 py-1.5 text-xs text-emerald-400/60 hover:text-emerald-400 transition-colors"
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}

const DEPT_BANNER_KEY = 'copapp.deptBannerDismissed';

/**
 * Onboarding nudge for solo accounts: the two department funnels (join by
 * invite, upgrade as the buyer) both start here. Dismiss persists locally.
 */
function DepartmentOnboardingBanner() {
  const { officer } = useSessionStore();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DEPT_BANNER_KEY) === '1');

  const hasDepartment = Boolean((officer as { departmentId?: string | null } | null)?.departmentId);
  if (!officer || hasDepartment || dismissed) return null;

  return (
    <div className="mb-6 bg-copapp-navy border border-copapp-line rounded-lg p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm text-fg font-medium">Training with a department?</p>
        <p className="text-xs text-fg-faint mt-0.5">
          Got an invite link from your admin? Open it to join — your history comes with you.
          Setting it up for your agency? Create your department in Settings.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <a href="/settings" className="px-3 py-1.5 bg-copapp-700 hover:bg-copapp-600 rounded-lg text-xs font-semibold text-white transition-colors">
          Set up my agency
        </a>
        <button
          onClick={() => { localStorage.setItem(DEPT_BANNER_KEY, '1'); setDismissed(true); }}
          className="px-2 py-1.5 text-xs text-fg-faint hover:text-fg transition-colors"
          aria-label="Dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function Dashboard() {
  const [debriefSessionId, setDebriefSessionId] = useState<string | null>(null);
  const { initSession, setPhase } = useSimulationStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { launchById } = useSessionLauncher();
  const { toast } = useToast();

  if (debriefSessionId) {
    return <DebriefView sessionId={debriefSessionId} onBack={() => setDebriefSessionId(null)} />;
  }

  const handleSelectSession = async (sessionId: string, mode: 'resume' | 'debrief') => {
    if (mode === 'debrief') {
      setDebriefSessionId(sessionId);
    } else if (user?.uid) {
      const { loadSession } = await import('./services/sessions');
      const session = await loadSession(user.uid, sessionId);
      if (session) {
        const metrics = session.metrics as { currentTurn?: number; totalRealTime?: number; totalSimTime?: string } | undefined;
        const simTimeStr = metrics?.totalSimTime || '0 seconds';
        const simTimeMatch = simTimeStr.match(/(\d+)\s*minute/);
        const simTimeSec = simTimeMatch?.[1] ? parseInt(simTimeMatch[1]) * 60 : 0;

        initSession({
          sessionId,
          officerUid: user.uid,
          departmentId: (session.departmentId as string) || null,
          scenario: session.scenario as never,
          worldState: (session.worldState as { public: unknown })?.public as never,
          turns: (session.turnHistory as never[]) || [],
          currentTurnNumber: metrics?.currentTurn || 0,
          simulatedTimeSeconds: simTimeSec,
          realTimeAccumulatedMs: metrics?.totalRealTime || 0,
        });
        setPhase('active');
        navigate('/sim');
      }
    }
  };

  // Same launcher as the debrief tridoor — one door for entering sessions.
  const handleMastery = async (sessionId: string, type: 'replay' | 'followup') => {
    try {
      const { createMasteryReplay, createMasteryFollowup } = await import('./services/mastery');
      const result = type === 'replay'
        ? await createMasteryReplay(sessionId)
        : await createMasteryFollowup(sessionId);
      if (result.sessionId) await launchById(result.sessionId);
    } catch (err) {
      reportCrash(err);
      toast(type === 'replay' ? "Couldn't start the replay — try again." : "Couldn't start the follow-up — try again.", 'error');
    }
  };

  return (
    <PageWrapper title="Dashboard">
      {/* Arrival (W12): where you stand, before anything asks anything —
          then one line of focus for the sitting. */}
      <YouStrip />
      <BriefingLine />

      <JoinFlashBanner />
      <CoverageBanner />
      <DepartmentOnboardingBanner />

      {/* The rhythm leads: this week's drill, then the certification path */}
      <WeeklyDrillCard />
      <CertificationPathCard />

      {/* Quick Actions — mirrored in DemoDashboard (docs/DEMO.md) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <a href="/sim" className="bg-copapp-700 hover:bg-copapp-600 rounded-lg p-4 text-center transition-colors">
          <span className="text-2xl block mb-1">▶</span>
          <span className="text-xs font-medium text-white">New Simulation</span>
        </a>
        <a href="/training" className="bg-copapp-navy border border-copapp-line hover:border-copapp-line-bright rounded-lg p-4 text-center transition-colors">
          <span className="text-2xl block mb-1">⬡</span>
          <span className="text-xs font-medium text-fg-muted">Training Modes</span>
        </a>
        <a href="/insights" className="bg-copapp-navy border border-copapp-line hover:border-copapp-line-bright rounded-lg p-4 text-center transition-colors">
          <span className="text-2xl block mb-1">◈</span>
          <span className="text-xs font-medium text-fg-muted">Insights</span>
        </a>
        <a href="/department" className="bg-copapp-navy border border-copapp-line hover:border-copapp-line-bright rounded-lg p-4 text-center transition-colors">
          <span className="text-2xl block mb-1">◫</span>
          <span className="text-xs font-medium text-fg-muted">Department</span>
        </a>
      </div>

      {/* Session History */}
      <h2 className="text-lg font-medium text-fg-muted mb-3">Recent Sessions</h2>
      <SessionHistory onSelectSession={handleSelectSession} onMastery={handleMastery} />
    </PageWrapper>
  );
}

function DepartmentPage() {
  return <PageWrapper title="Department" wide><DepartmentView /></PageWrapper>;
}

function TrainingPage() {
  return <PageWrapper title="Training"><TrainingHub /></PageWrapper>;
}

function InsightsPage() {
  return <PageWrapper title="Insights" wide><InsightsDashboard /></PageWrapper>;
}

/**
 * Community Connect route gate — the open-system front door. Signed-in
 * officers get it inside the app shell; the public gets it standalone
 * with minimal chrome. Same component either way; publish and "your
 * published calls" gate themselves on auth inside it.
 */
function CommunityRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="w-8 h-8 border-2 border-copapp-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  // Anonymous auth (challenge players) is a technical detail, not an
  // officer sign-in — they get the public page.
  if (isAuthenticated && user && !user.isAnonymous) {
    return <AppShell><CommunityConnect /></AppShell>;
  }
  return (
    <DarkPinned>
      <div className="min-h-screen bg-copapp-dark">
        <header className="border-b border-copapp-line">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <a href="/" className="text-fg font-bold tracking-tight">CopApp</a>
            <nav className="flex items-center gap-4">
              <a href="/login" className="text-sm text-fg-muted hover:text-fg transition-colors">Sign In</a>
              <a href="/signup" className="px-4 py-1.5 bg-copapp-700 hover:bg-copapp-600 rounded-lg text-sm font-semibold text-white transition-colors">
                Start training
              </a>
            </nav>
          </div>
        </header>
        <CommunityConnect />
      </div>
    </DarkPinned>
  );
}

function SettingsWrapper() {
  return <PageWrapper title="Settings"><SettingsPage /></PageWrapper>;
}

function SimulationRouter() {
  const phase = useSimulationStore((s) => s.phase);

  if (phase === 'idle' || phase === 'scenario-select' || phase === 'framework-config') {
    return <ScenarioSelect />;
  }

  if (phase === 'generating-scenario') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-copapp-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-gray-400">Generating scenario...</p>
        </div>
      </div>
    );
  }

  return <SimulationView />;
}
